import { useAuth } from "@clerk/clerk-react";
import { Grid, Skeleton, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { BentoCategory } from "schemas/dashboard";
import { Map } from "schemas/functions";

import StyledCard from "components/Styled/Card";
import QuestionChip from "components/Styled/QuestionChip";
import { fetcherAuth } from "utils/api";
import { getSource } from "utils/tracking";
import { makeDeepCopy } from "utils/updateLocalState";

import StyledOnboardingPage from "../StyledOnboardingPage";
import styles from "./styles";

interface Props {
  handleBack: () => void;
  handleSkip?: () => void;
  onboardingForm: Map;
  moveNext?: () => void;
  isVaOnboarding?: boolean;
  setNeedsUpdateCategory?: React.Dispatch<React.SetStateAction<boolean>>;
}

const generateWidths = (count: number, min = 85, max = 200) => {
  return Array.from(
    { length: count },
    () => `${Math.floor(Math.random() * (max - min + 1)) + min}px`,
  );
};

const CategoryForm = ({
  handleBack,
  handleSkip,
  moveNext,
  isVaOnboarding = false,
  setNeedsUpdateCategory,
}: Props) => {
  const { referralCode } = useContext(SubscriptionContext);
  const { setErrorAlert, setAlert } = useContext(AlertContext);
  const { currentOrg, setCurrentOrg } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();
  const location = useLocation();
  const source = getSource();

  const [selectedCategories, setSelectedCategories] = useState<BentoCategory[]>(
    currentOrg?.bentoCategories || [],
  );

  const [categoriesToPickFrom, setCategoriesToPickFrom] = useState<
    BentoCategory[]
  >([]);

  const handleSubmit = async () => {
    if (selectedCategories?.length === 0) {
      setAlert("Please select at least one category to continue.", "warning");
      return;
    }
    if (moveNext) moveNext();
    try {
      const { categories } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding/categories`,
        "PUT",
        {},
        {
          categoryIds: selectedCategories?.map((x) => x.id),
          page: location.pathname,
          source: referralCode ? "referral" : source,
          isVaFlow: isVaOnboarding,
          isOnboarding: true,
        },
      );

      setCurrentOrg((prev) => {
        const copy = makeDeepCopy(prev);
        copy.bentoCategories = categories;
        return copy;
      });
    } catch (error) {
      setErrorAlert(error);
      handleBack();
    }
  };

  const getCategoriesToPickFrom = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/bento-categories/onboarding`,
      );
      setCategoriesToPickFrom(res.onboardingBentoCategories);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const isSelected = (chip: BentoCategory) => {
    return selectedCategories?.some((x: BentoCategory) => x.id === chip.id);
  };

  const addCategory = (copy: BentoCategory[], category: BentoCategory) => {
    const index = copy.findIndex((x: BentoCategory) => x.id === category.id);
    if (index > -1) {
      copy.splice(index, 1);
    } else {
      copy.push(category);
    }
    return copy;
  };

  const selectChip = (chip: BentoCategory) => {
    if (setNeedsUpdateCategory) setNeedsUpdateCategory(true);
    setSelectedCategories((prev) => {
      const copy = [...prev];
      addCategory(copy, chip);
      return copy;
    });
  };

  useEffect(() => {
    if (currentOrg?.id) {
      getCategoriesToPickFrom();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  const loadingElements = useMemo(
    () => generateWidths(23),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    setSelectedCategories(currentOrg?.bentoCategories || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.bentoCategories?.length]);

  return (
    <StyledOnboardingPage
      title={"Your Brand Types 🗂️"}
      handleBack={handleBack}
      handleSkip={handleSkip}
      handleNext={handleSubmit}
    >
      <StyledCard styles={{ px: 3, py: 4 }}>
        <Typography gutterBottom>
          What brand categories do you want to see?
        </Typography>
        <Typography variant="caption" color="textSecondary" paragraph>
          Select all categories you want to see. You can always change this
          later.
        </Typography>

        <Grid container sx={{ my: 2 }} gap={1}>
          {categoriesToPickFrom.length === 0 &&
            loadingElements.map((width, i) => (
              <Skeleton
                key={i}
                variant="rounded"
                width={width}
                sx={styles.loadingSkeleton}
              />
            ))}
          {categoriesToPickFrom?.map((category, index) => (
            <QuestionChip
              key={`category-${index}`}
              onClick={() => selectChip(category)}
              isSelected={isSelected(category)}
              label={category.name}
            />
          ))}
        </Grid>
      </StyledCard>
    </StyledOnboardingPage>
  );
};

export default CategoryForm;
